import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate } from '@tanstack/react-router'
import jsQR from 'jsqr'
import {
  AlertTriangle,
  ArrowDownAZ,
  ArrowUpAZ,
  CalendarDays,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ChevronUp,
  Clock,
  Download,
  FileText,
  Filter,
  Grid3x3,
  Inbox,
  KeyRound,
  LayoutList,
  Loader2,
  Mail,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  BulkActionBar,
  EmptyTable,
  Pager,
  RowCheckbox,
  SelectAllCheckbox,
  SortableHeader,
  TableToolbar,
} from '../components/data-table'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot, SheetRow } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { TabBadge, TabNav, tabPillClass } from '../components/ui/tab-nav'
import { TableShell } from '../components/ui/table-shell'
import {
  api,
  type DocSignature,
  type DocumentSummary,
  type FetchedDocument,
  type FetchedDocumentStatus,
  type FetchedDocumentType,
  type FetcherAccount,
  type FetcherProvider as FetcherProviderType,
} from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { useNotificationStore } from '../lib/notification-store'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

type Tab = 'files' | 'mailbox' | 'fetched'
type SortKey = 'name' | 'date' | 'size'
type SortDir = 'asc' | 'desc'
type ViewMode = 'list' | 'grid'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  validateSearch: (s: Record<string, unknown>) => ({
    tab: (s.tab === 'mailbox' ? 'mailbox' : s.tab === 'fetched' ? 'fetched' : 'files') as Tab,
  }),
  component: DocumentsPage,
})

function DocumentsPage() {
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  // Two parallel queries so the mailbox badge stays accurate even when the
  // user is on the Meine-Dateien tab. Both are cheap (single SELECT each).
  const { data: outgoingData, isLoading: outgoingLoading } = useQuery({
    queryKey: ['documents', { incoming: false }],
    queryFn: () => api.listDocuments({ incoming: false }),
    enabled: !!meData?.user,
  })
  const { data: incomingData } = useQuery({
    queryKey: ['documents', { incoming: true }],
    queryFn: () => api.listDocuments({ incoming: true }),
    enabled: !!meData?.user,
  })
  const isAdmin = meData?.user?.role === 'admin'

  // Fetched documents (Belegeingang) — admin-only
  const { data: fetchedDocsData } = useQuery({
    queryKey: ['fetcher', 'documents'],
    queryFn: () => api.listFetchedDocuments(),
    enabled: isAdmin,
  })
  const { data: fetcherAccountsData } = useQuery({
    queryKey: ['fetcher', 'accounts'],
    queryFn: () => api.listFetcherAccounts(),
    enabled: isAdmin,
    // Only poll fast while a sync is actually in flight; otherwise this
    // fires ~17k idle requests/day per open tab. Fall back to a slow
    // 60s refresh so a sync started elsewhere is still picked up.
    refetchInterval: (query) =>
      query.state.data?.accounts.some((a) => a.status === 'syncing' || a.status === 'awaiting-2fa')
        ? 5000
        : 60_000,
  })
  const { data: fetcherProvidersData } = useQuery({
    queryKey: ['fetcher', 'providers'],
    queryFn: () => api.listFetcherProviders(),
    enabled: isAdmin,
  })

  const { data: usersData } = useQuery({
    queryKey: ['documents', 'sendable-users'],
    queryFn: () => api.documentSendableUsers(),
    enabled: !!meData?.user,
  })
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects({ archived: false }),
    enabled: !!meData?.user,
  })

  // === Fetcher sync status → Notification Center ===
  const prevSyncRef = useRef<Map<string, string>>(new Map())
  const addNotification = useNotificationStore((s) => s.add)
  useEffect(() => {
    const accounts = fetcherAccountsData?.accounts ?? []
    for (const acc of accounts) {
      const prev = prevSyncRef.current.get(acc.id)
      if (prev && prev !== acc.status) {
        if (acc.status === 'syncing') {
          addNotification('info', `Belegabruf „${acc.label}“: Synchronisation gestartet`)
        } else if (acc.status === 'active' && prev === 'syncing') {
          const count = acc.lastSyncCount ?? 0
          addNotification(
            'success',
            `Belegabruf „${acc.label}“: ${count} neue Belege synchronisiert`,
          )
        } else if (acc.status === 'error') {
          addNotification(
            'error',
            `Belegabruf „${acc.label}“: ${acc.lastSyncError?.slice(0, 100) ?? 'Fehler'}`,
          )
        } else if (acc.status === 'awaiting-2fa') {
          addNotification('warning', `Belegabruf „${acc.label}“: 2FA-Code erforderlich`)
        }
      }
      prevSyncRef.current.set(acc.id, acc.status)
    }
  }, [fetcherAccountsData, addNotification])

  // === Files-tab toolbar state ===
  const [search, setSearch] = useState('')
  const [projectFilter, setProjectFilter] = useState<'all' | 'none' | string>('all')
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [view, setView] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'list'
    return (localStorage.getItem('documents.view') as ViewMode | null) ?? 'list'
  })
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('documents.view', view)
  }, [view])

  // Multi-select state — only meaningful on the Files tab
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const clearSelection = () => setSelected(new Set())

  // Dialogs
  const [uploadOpen, setUploadOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [renameTarget, setRenameTarget] = useState<DocumentSummary | null>(null)
  const [sendTarget, setSendTarget] = useState<DocumentSummary | null>(null)

  // Mobile-Shell (<640): Titel oben; Suche/Projekt-Filter als Sheets (nur
  // auf dem Dateien-Tab — Desktop-Parität, die Toolbar existiert nur dort)
  // + ＋ Upload auf allen Tabs. Die Tab-Zeile bleibt im Content.
  useMobilePageTitle('Dateien')
  const [mobileSheet, setMobileSheet] = useState<null | 'search' | 'project'>(null)

  const uploadMutation = useMutation({ mutationFn: api.uploadDocument })
  const sendMutation = useMutation({
    mutationFn: ({
      id,
      recipientUserIds,
      message,
    }: {
      id: string
      recipientUserIds: string[]
      message: string | null
    }) => api.sendDocument(id, { recipientUserIds, message }),
  })
  const bulkArchive = useMutation({
    mutationFn: (ids: string[]) => api.bulkArchiveDocuments(ids),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      clearSelection()
      if (r.failed === 0) notify.success(`${r.archived} Dateien gelöscht`)
      else notify.warning(`${r.archived} gelöscht, ${r.failed} fehlgeschlagen (keine Rechte)`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // === Derived ===
  const myDocs = outgoingData?.documents ?? []
  const myDocSigs = outgoingData?.signatures ?? []
  const incomingDocs = incomingData?.documents ?? []
  const incomingSigs = incomingData?.signatures ?? []
  const projects = projectsData?.projects ?? []

  // Filter pipeline for Meine Dateien
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return myDocs.filter((d) => {
      if (q && !d.filename.toLowerCase().includes(q)) return false
      if (projectFilter === 'none' && d.projectId !== null) return false
      if (projectFilter !== 'all' && projectFilter !== 'none' && d.projectId !== projectFilter)
        return false
      return true
    })
  }, [myDocs, search, projectFilter])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    const dir = sortDir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.filename.localeCompare(b.filename, 'de') * dir
        case 'size':
          return (a.sizeBytes - b.sizeBytes) * dir
        default:
          return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
      }
    })
    return rows
  }, [filtered, sortKey, sortDir])

  // Bulk-actions only operate on visible rows so a hidden selection can't
  // surprise the user.
  const visibleSelectedIds = sorted.filter((d) => selected.has(d.id)).map((d) => d.id)

  // Mailbox-tab data: pending signatures (= unread badge) + sent docs
  const pendingInbox = incomingSigs.filter((s) => s.status === 'pending').length
  // "Sent" = my uploads that have at least one signature row
  const sentDocs = useMemo(() => {
    const docIdsWithSigs = new Set(myDocSigs.map((s) => s.documentId))
    return myDocs.filter((d) => docIdsWithSigs.has(d.id))
  }, [myDocs, myDocSigs])

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const myId = meData.user.id
  const allUsers = usersData?.users ?? []
  const userById = new Map(allUsers.map((u) => [u.id, u]))
  const projectById = new Map(projects.map((p) => [p.id, p]))

  // === Drag-and-drop handlers — active everywhere on the page ===
  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const files = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    )
    if (files.length === 0) {
      notify.error('Nur PDF-Dateien erlaubt')
      return
    }
    void uploadMany(files)
  }

  async function uploadMany(files: File[]) {
    let ok = 0
    for (const f of files) {
      try {
        await uploadMutation.mutateAsync(f)
        ok++
      } catch (err) {
        notify.error(`${f.name}: ${(err as Error).message}`)
      }
    }
    if (ok > 0) {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      notify.success(`${ok} Dateien hochgeladen`)
    }
  }

  function setTab(next: Tab) {
    navigate({ search: { tab: next } })
  }

  function bulkDelete() {
    const ids = visibleSelectedIds
    if (ids.length === 0) return
    void confirm({
      title: `${ids.length} Datei${ids.length === 1 ? '' : 'en'} löschen?`,
      description: 'Die Dateien werden archiviert und sind danach nicht mehr in der Liste.',
      variant: 'danger',
      confirmLabel: 'Löschen',
    }).then((ok) => {
      if (ok) bulkArchive.mutate(ids)
    })
  }

  const allSelectedOnPage = sorted.length > 0 && sorted.every((d) => selected.has(d.id))
  const someSelectedOnPage = sorted.some((d) => selected.has(d.id)) && !allSelectedOnPage
  function toggleAllOnPage() {
    if (allSelectedOnPage) {
      setSelected((prev) => {
        const next = new Set(prev)
        for (const d of sorted) next.delete(d.id)
        return next
      })
    } else {
      setSelected((prev) => {
        const next = new Set(prev)
        for (const d of sorted) next.add(d.id)
        return next
      })
    }
  }

  return (
    <section
      aria-label="Dateien — Drag-and-Drop zum Hochladen"
      className="space-y-6 animate-fade-in relative"
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragOver(false)
      }}
      onDrop={handleDrop}
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche + Projekt-Filter (nur Dateien-Tab,
          Desktop-Parität) · ＋ Upload (alle Tabs, gleicher Handler). */}
      <MobileBottomSlots>
        {tab === 'files' && (
          <BarSlot
            label="Suche"
            icon={<Search className="h-5 w-5" />}
            active={search !== ''}
            ariaLabel="Dateien durchsuchen"
            ariaHasPopup
            onClick={() => setMobileSheet('search')}
          />
        )}
        {tab === 'files' && (
          <BarSlot
            label="Projekt"
            icon={<Filter className="h-5 w-5" />}
            active={projectFilter !== 'all'}
            ariaLabel="Nach Projekt filtern"
            ariaHasPopup
            onClick={() => setMobileSheet('project')}
          />
        )}
        <BarAccentPlus ariaLabel="PDF hochladen" onClick={() => setUploadOpen(true)} />
      </MobileBottomSlots>

      {/* Suche-Sheet (<640): bindet dieselbe search-State wie die Toolbar. */}
      <SidePanel open={mobileSheet === 'search'} onClose={() => setMobileSheet(null)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* Projekt-Filter-Sheet (<640): dieselbe projectFilter-State wie das
          Toolbar-Select (Alle / Ohne Projekt / je Projekt). */}
      <SidePanel
        open={mobileSheet === 'project'}
        onClose={() => setMobileSheet(null)}
        title="Projekt-Filter"
      >
        <div className="flex flex-col gap-1">
          <SheetRow
            active={projectFilter === 'all'}
            label="Alle Projekte"
            onClick={() => setProjectFilter('all')}
          />
          <SheetRow
            active={projectFilter === 'none'}
            label="Ohne Projekt"
            onClick={() => setProjectFilter('none')}
          />
          {projects.map((p) => (
            <SheetRow
              key={p.id}
              active={projectFilter === p.id}
              label={p.name}
              onClick={() => setProjectFilter(p.id)}
            />
          ))}
        </div>
      </SidePanel>

      {/* Einreihiger Kopf: Tabs bleiben am Phone sichtbar; Titel und Upload
          übernimmt dort die Shell. */}
      <PageHeader
        title="Dateien"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Dateien-Bereiche">
            <button
              type="button"
              className={tabPillClass(tab === 'files')}
              aria-pressed={tab === 'files'}
              onClick={() => setTab('files')}
            >
              <FileText className="h-4 w-4 shrink-0" />
              Meine Dateien
              <span className="text-text-tertiary tabular-nums">({myDocs.length})</span>
            </button>
            <button
              type="button"
              className={tabPillClass(tab === 'mailbox')}
              aria-pressed={tab === 'mailbox'}
              onClick={() => setTab('mailbox')}
            >
              <Mail className="h-4 w-4 shrink-0" />
              Postfach
              <TabBadge count={pendingInbox} />
            </button>
            {isAdmin && (
              <button
                type="button"
                className={tabPillClass(tab === 'fetched')}
                aria-pressed={tab === 'fetched'}
                onClick={() => setTab('fetched')}
              >
                <Download className="h-4 w-4 shrink-0" />
                Belegeingang
                <TabBadge
                  count={
                    (fetchedDocsData?.documents ?? []).filter((d) => d.status === 'new').length
                  }
                />
              </button>
            )}
          </TabNav>
        }
        actions={
          <Button className="phone:hidden" onClick={() => setUploadOpen(true)} variant="primary">
            <Upload className="h-4 w-4" />
            PDF hochladen
          </Button>
        }
      />

      {tab === 'files' ? (
        <FilesView
          docs={sorted}
          allDocs={myDocs}
          isLoading={outgoingLoading}
          search={search}
          onSearch={setSearch}
          projectFilter={projectFilter}
          onProjectFilter={setProjectFilter}
          projects={projects}
          projectById={projectById}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortKey={setSortKey}
          onSortDir={setSortDir}
          view={view}
          onView={setView}
          selected={selected}
          visibleSelectedCount={visibleSelectedIds.length}
          onToggleSelected={toggleSelected}
          allSelected={allSelectedOnPage}
          someSelected={someSelectedOnPage}
          onToggleAll={toggleAllOnPage}
          onClearSelection={clearSelection}
          onBulkDelete={bulkDelete}
          bulkDeleting={bulkArchive.isPending}
          onRename={setRenameTarget}
          onSend={setSendTarget}
          onUpload={() => setUploadOpen(true)}
        />
      ) : tab === 'fetched' ? (
        <FetchedDocsView
          documents={fetchedDocsData?.documents ?? []}
          accounts={fetcherAccountsData?.accounts ?? []}
          providers={fetcherProvidersData?.providers ?? []}
        />
      ) : (
        <MailboxView
          incomingDocs={incomingDocs}
          incomingSigs={incomingSigs}
          sentDocs={sentDocs}
          sentSigs={myDocSigs}
          userById={userById}
          myId={myId}
        />
      )}

      {/* Drag overlay */}
      {dragOver && (
        <div className="fixed inset-0 z-30 pointer-events-none flex items-center justify-center bg-accent/10 backdrop-blur-sm border-4 border-dashed border-accent">
          <div className="rounded-2xl bg-bg-elevated px-8 py-6 text-center shadow-2xl">
            <Upload className="mx-auto h-10 w-10 text-accent" />
            <p className="mt-2 text-lg font-medium">PDF hier ablegen</p>
            <p className="text-sm text-text-tertiary">Mehrere Dateien gleichzeitig möglich</p>
          </div>
        </div>
      )}

      {uploadOpen && (
        <UploadDialog
          allUsers={allUsers}
          projects={projects}
          onClose={() => setUploadOpen(false)}
          onSubmit={async ({ file, recipientUserIds, message, projectId }) => {
            try {
              const { document } = await uploadMutation.mutateAsync(file)
              if (projectId) await api.updateDocument(document.id, { projectId })
              if (recipientUserIds.length > 0) {
                await sendMutation.mutateAsync({
                  id: document.id,
                  recipientUserIds,
                  message: message || null,
                })
                notify.success(`Hochgeladen und an ${recipientUserIds.length} Empfänger verschickt`)
              } else {
                notify.success('Hochgeladen')
              }
              queryClient.invalidateQueries({ queryKey: ['documents'] })
              setUploadOpen(false)
            } catch (err) {
              notify.error((err as Error).message)
            }
          }}
        />
      )}

      {renameTarget && (
        <RenameDialog
          doc={renameTarget}
          projects={projects}
          onClose={() => setRenameTarget(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['documents'] })
            setRenameTarget(null)
          }}
        />
      )}

      {sendTarget && (
        <SendDialog
          doc={sendTarget}
          allUsers={allUsers}
          onClose={() => setSendTarget(null)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['documents'] })
            setSendTarget(null)
          }}
        />
      )}
    </section>
  )
}

// ============================================================================
// Files view — the actual file manager
// ============================================================================

interface FilesViewProps {
  docs: DocumentSummary[]
  allDocs: DocumentSummary[]
  isLoading: boolean
  search: string
  onSearch: (v: string) => void
  projectFilter: 'all' | 'none' | string
  onProjectFilter: (v: 'all' | 'none' | string) => void
  projects: { id: string; name: string }[]
  projectById: Map<string, { id: string; name: string }>
  sortKey: SortKey
  sortDir: SortDir
  onSortKey: (k: SortKey) => void
  onSortDir: (d: SortDir | ((prev: SortDir) => SortDir)) => void
  view: ViewMode
  onView: (v: ViewMode) => void
  selected: Set<string>
  visibleSelectedCount: number
  onToggleSelected: (id: string) => void
  allSelected: boolean
  someSelected: boolean
  onToggleAll: () => void
  onClearSelection: () => void
  onBulkDelete: () => void
  bulkDeleting: boolean
  onRename: (d: DocumentSummary) => void
  onSend: (d: DocumentSummary) => void
  onUpload: () => void
}

function FilesView(props: FilesViewProps) {
  const {
    docs,
    allDocs,
    isLoading,
    search,
    onSearch,
    projectFilter,
    onProjectFilter,
    projects,
    projectById,
    sortKey,
    sortDir,
    onSortKey,
    onSortDir,
    view,
    onView,
    selected,
    visibleSelectedCount,
    onToggleSelected,
    allSelected,
    someSelected,
    onToggleAll,
    onClearSelection,
    onBulkDelete,
    bulkDeleting,
    onRename,
    onSend,
    onUpload,
  } = props
  return (
    <div className="space-y-4">
      {/* Toolbar — Suche + Projekt-Select übernimmt <640 das jeweilige
          Sheet der Bottom-Bar (phone:hidden); Sortierung + Ansicht
          bleiben auch mobil hier. */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-2">
        <div className="relative flex-1 min-w-[200px] phone:hidden">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-9"
          />
        </div>
        <Select
          value={projectFilter}
          onChange={(e) => onProjectFilter(e.target.value as typeof projectFilter)}
          className="phone:hidden w-48"
          aria-label="Projekt filtern"
        >
          <option value="all">Alle Projekte</option>
          <option value="none">Ohne Projekt</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <div className="inline-flex items-center gap-1">
          <Select
            value={sortKey}
            onChange={(e) => onSortKey(e.target.value as SortKey)}
            className="w-32"
            aria-label="Sortieren nach"
          >
            <option value="date">Datum</option>
            <option value="name">Name</option>
            <option value="size">Größe</option>
          </Select>
          <button
            type="button"
            onClick={() => onSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            aria-label={sortDir === 'asc' ? 'Aufsteigend' : 'Absteigend'}
            className="p-2.5 rounded-lg border border-border-default bg-bg-overlay/50 text-text-secondary hover:text-text-primary hover:bg-bg-overlay"
          >
            {sortDir === 'asc' ? (
              <ArrowUpAZ className="h-4 w-4" />
            ) : (
              <ArrowDownAZ className="h-4 w-4" />
            )}
          </button>
        </div>
        <div className="inline-flex rounded-lg bg-bg-overlay/40 p-0.5">
          <ViewChip active={view === 'list'} onClick={() => onView('list')} aria-label="Liste">
            <LayoutList className="h-4 w-4" />
          </ViewChip>
          <ViewChip active={view === 'grid'} onClick={() => onView('grid')} aria-label="Kacheln">
            <Grid3x3 className="h-4 w-4" />
          </ViewChip>
        </div>
      </div>

      {/* Bulk action bar */}
      {visibleSelectedCount > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-accent/40 bg-accent/5 px-4 py-2.5">
          <p className="text-sm text-text-primary">
            <span className="font-medium">{visibleSelectedCount}</span> ausgewählt
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClearSelection}>
              <X className="h-3.5 w-3.5" /> Auswahl aufheben
            </Button>
            <Button variant="danger" size="sm" onClick={onBulkDelete} disabled={bulkDeleting}>
              <Trash2 className="h-3.5 w-3.5" /> Löschen
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-text-tertiary">Lade …</p>
      ) : docs.length === 0 ? (
        <FilesEmptyState
          totalDocs={allDocs.length}
          hasFilter={!!search || projectFilter !== 'all'}
          onUpload={onUpload}
        />
      ) : view === 'list' ? (
        <FileList
          docs={docs}
          projectById={projectById}
          selected={selected}
          onToggleSelected={onToggleSelected}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleAll={onToggleAll}
          onRename={onRename}
          onSend={onSend}
        />
      ) : (
        <FileGrid
          docs={docs}
          projectById={projectById}
          selected={selected}
          onToggleSelected={onToggleSelected}
          onRename={onRename}
          onSend={onSend}
        />
      )}
    </div>
  )
}

function FilesEmptyState({
  totalDocs,
  hasFilter,
  onUpload,
}: {
  totalDocs: number
  hasFilter: boolean
  onUpload: () => void
}) {
  if (hasFilter) {
    return (
      <div className="card-elevated px-6 py-16 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
          <Filter className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <p className="mt-4 text-base font-medium text-text-primary">Keine Treffer</p>
        <p className="mt-1 text-sm text-text-tertiary">Suche oder Filter anpassen.</p>
      </div>
    )
  }
  if (totalDocs === 0) {
    return (
      <div className="card-elevated px-6 py-16 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
          <FileText className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <p className="mt-4 text-base font-medium text-text-primary">Noch keine Dateien</p>
        <p className="mt-1 text-sm text-text-tertiary max-w-sm mx-auto">
          Lade mit „PDF hochladen“ dein erstes Dokument hoch — oder ziehe eine Datei hierher.
          Anschließend kannst du es über „An Empfänger senden“ zur Unterschrift verschicken.
        </p>
        <Button onClick={onUpload} className="mt-5">
          <Upload className="h-4 w-4" /> PDF hochladen
        </Button>
      </div>
    )
  }
  return null
}

interface FileListProps {
  docs: DocumentSummary[]
  projectById: Map<string, { id: string; name: string }>
  selected: Set<string>
  onToggleSelected: (id: string) => void
  allSelected: boolean
  someSelected: boolean
  onToggleAll: () => void
  onRename: (d: DocumentSummary) => void
  onSend: (d: DocumentSummary) => void
}

function FileList(props: FileListProps) {
  const {
    docs,
    projectById,
    selected,
    onToggleSelected,
    allSelected,
    someSelected,
    onToggleAll,
    onRename,
    onSend,
  } = props
  return (
    <TableShell className="rounded-2xl bg-bg-elevated/40">
      <table className="w-full text-sm table-fixed">
        <thead className="bg-bg-overlay/40">
          <tr className="text-left">
            <th className="pl-5 pr-2 py-2.5 w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected
                }}
                onChange={onToggleAll}
                className="accent-accent h-4 w-4"
                aria-label="Alle auswählen"
              />
            </th>
            <th className="px-5 py-2.5 text-xs uppercase tracking-wider text-text-tertiary font-medium">
              Name
            </th>
            <th className="px-5 py-2.5 w-44 text-xs uppercase tracking-wider text-text-tertiary font-medium">
              Projekt
            </th>
            <th className="px-5 py-2.5 w-32 text-right text-xs uppercase tracking-wider text-text-tertiary font-medium">
              Datum
            </th>
            <th className="px-5 py-2.5 w-24 text-right text-xs uppercase tracking-wider text-text-tertiary font-medium">
              Größe
            </th>
            <th className="px-5 py-2.5 w-32" aria-label="Aktionen" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {docs.map((doc) => {
            const isSel = selected.has(doc.id)
            return (
              <tr
                key={doc.id}
                className={cn(
                  'group transition-colors hover:bg-bg-overlay/40',
                  isSel && 'bg-accent/5',
                )}
              >
                <td className="pl-5 pr-2 py-3">
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => onToggleSelected(doc.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-accent h-4 w-4"
                    aria-label={`${doc.filename} auswählen`}
                  />
                </td>
                <td className="px-5 py-3">
                  <Link
                    to="/documents/$id"
                    params={{ id: doc.id }}
                    className="flex items-center gap-3 min-w-0"
                  >
                    <FileText className="h-4 w-4 text-text-tertiary shrink-0" />
                    <span className="truncate font-medium text-text-primary" title={doc.filename}>
                      {doc.filename}
                    </span>
                  </Link>
                </td>
                <td className="px-5 py-3">
                  {doc.projectId ? (
                    <span className="inline-block max-w-full truncate text-xs px-2 py-0.5 rounded-md bg-accent/10 text-accent">
                      {projectById.get(doc.projectId)?.name ?? '—'}
                    </span>
                  ) : (
                    <span className="text-xs text-text-tertiary">—</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right text-xs text-text-tertiary tabular-nums whitespace-nowrap">
                  {new Date(doc.createdAt).toLocaleDateString('de-DE')}
                </td>
                <td className="px-5 py-3 text-right text-xs text-text-tertiary tabular-nums whitespace-nowrap">
                  {formatBytes(doc.sizeBytes)}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onSend(doc)}
                      aria-label="An Empfänger senden"
                      title="An Empfänger senden"
                      className="text-text-tertiary hover:text-accent"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onRename(doc)}
                      aria-label="Umbenennen / Projekt setzen"
                      title="Umbenennen / Projekt setzen"
                      className="text-text-tertiary hover:text-accent"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <a
                      href={api.documentFileUrl(doc.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Herunterladen"
                      title="Herunterladen"
                      className="text-text-tertiary hover:text-accent transition-colors p-2 rounded-md hover:bg-bg-overlay"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}

interface FileGridProps {
  docs: DocumentSummary[]
  projectById: Map<string, { id: string; name: string }>
  selected: Set<string>
  onToggleSelected: (id: string) => void
  onRename: (d: DocumentSummary) => void
  onSend: (d: DocumentSummary) => void
}

function FileGrid(props: FileGridProps) {
  const { docs, projectById, selected, onToggleSelected, onRename, onSend } = props
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {docs.map((doc) => {
        const isSel = selected.has(doc.id)
        return (
          <div
            key={doc.id}
            className={cn(
              'group relative rounded-2xl border bg-bg-elevated/40 p-4 transition-colors hover:border-border-default',
              isSel ? 'border-accent bg-accent/5' : 'border-border-subtle',
            )}
          >
            <input
              type="checkbox"
              checked={isSel}
              onChange={() => onToggleSelected(doc.id)}
              className={cn(
                'absolute top-3 left-3 accent-accent h-4 w-4 transition-opacity',
                isSel ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 touch:opacity-100',
              )}
              aria-label={`${doc.filename} auswählen`}
            />
            <Link to="/documents/$id" params={{ id: doc.id }} className="block">
              <div className="flex h-32 items-center justify-center rounded-xl bg-bg-overlay/40 mb-3 group-hover:bg-bg-overlay/60 transition-colors">
                <FileText
                  className="h-12 w-12 text-text-tertiary group-hover:text-accent transition-colors"
                  strokeWidth={1.2}
                />
              </div>
              <p className="font-medium text-sm text-text-primary truncate" title={doc.filename}>
                {doc.filename}
              </p>
              {doc.projectId && (
                <span className="mt-1 inline-block text-2xs px-1.5 py-0.5 rounded bg-accent/10 text-accent truncate max-w-full">
                  {projectById.get(doc.projectId)?.name ?? '—'}
                </span>
              )}
              <div className="mt-1 flex items-center justify-between text-[11px] text-text-tertiary tabular-nums">
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  {new Date(doc.createdAt).toLocaleDateString('de-DE')}
                </span>
                <span>{formatBytes(doc.sizeBytes)}</span>
              </div>
            </Link>
            <div className="absolute top-2 right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
              <IconButton
                onClick={() => onSend(doc)}
                aria-label="An Empfänger senden"
                title="An Empfänger senden"
              >
                <Send className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton onClick={() => onRename(doc)} aria-label="Bearbeiten" title="Bearbeiten">
                <Pencil className="h-3.5 w-3.5" />
              </IconButton>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ============================================================================
// Mailbox view — Eingang + Ausgang stacked as mail-style sections
// ============================================================================

interface MailboxViewProps {
  incomingDocs: DocumentSummary[]
  incomingSigs: DocSignature[]
  sentDocs: DocumentSummary[]
  sentSigs: DocSignature[]
  userById: Map<string, { name: string; email: string }>
  myId: string
}

function MailboxView(props: MailboxViewProps) {
  const { incomingDocs, incomingSigs, sentDocs, sentSigs, userById, myId } = props
  const pending = incomingSigs.filter((s) => s.status === 'pending').length

  return (
    <div className="space-y-6">
      <MailboxSection
        title="Eingang"
        subtitle={
          incomingDocs.length === 0
            ? 'Keine Dokumente zur Unterschrift erhalten.'
            : `${incomingDocs.length} Dokument${incomingDocs.length === 1 ? '' : 'e'}${
                pending > 0 ? ` · ${pending} offen` : ''
              }`
        }
        icon={<Inbox className="h-4 w-4" />}
      >
        {incomingDocs.length === 0 ? (
          <MailboxEmpty
            text="Sobald jemand dir ein Dokument zur Unterschrift schickt, taucht es hier auf."
            icon={<Inbox className="h-7 w-7" strokeWidth={1.5} />}
          />
        ) : (
          <MailList
            docs={incomingDocs}
            sigs={incomingSigs}
            myId={myId}
            kind="inbox"
            userById={userById}
          />
        )}
      </MailboxSection>

      <MailboxSection
        title="Ausgang"
        subtitle={
          sentDocs.length === 0
            ? 'Keine Dokumente zur Unterschrift verschickt.'
            : `${sentDocs.length} Dokument${sentDocs.length === 1 ? '' : 'e'}`
        }
        icon={<Send className="h-4 w-4" />}
      >
        {sentDocs.length === 0 ? (
          <MailboxEmpty
            text="Lade unter »Meine Dateien« ein PDF hoch und versende es dort über „An Empfänger senden“."
            icon={<Send className="h-7 w-7" strokeWidth={1.5} />}
          />
        ) : (
          <MailList docs={sentDocs} sigs={sentSigs} myId={myId} kind="outbox" userById={userById} />
        )}
      </MailboxSection>
    </div>
  )
}

function MailboxSection({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string
  subtitle: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section>
      <header className="flex items-center gap-2 mb-2">
        <span className="text-text-tertiary">{icon}</span>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">
          {title}
        </h2>
        <span className="text-xs text-text-tertiary">· {subtitle}</span>
      </header>
      {children}
    </section>
  )
}

function MailboxEmpty({ text, icon }: { text: string; icon: React.ReactNode }) {
  return (
    <div className="card-elevated px-6 py-10 text-center">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
        {icon}
      </div>
      <p className="mt-3 text-sm text-text-tertiary max-w-sm mx-auto">{text}</p>
    </div>
  )
}

function MailList({
  docs,
  sigs,
  myId,
  kind,
  userById,
}: {
  docs: DocumentSummary[]
  sigs: DocSignature[]
  myId: string
  kind: 'inbox' | 'outbox'
  userById: Map<string, { name: string; email: string }>
}) {
  return (
    <div className="card-elevated divide-y divide-border-subtle">
      {docs.map((doc) => {
        const docSigs = sigs.filter((s) => s.documentId === doc.id)
        const mySig = docSigs.find((s) => s.recipientUserId === myId)
        const status = kind === 'inbox' ? mySig?.status : aggregateStatus(docSigs)
        const counterparty =
          kind === 'inbox'
            ? (userById.get(mySig?.sentByUserId ?? '')?.name ?? '—')
            : docSigs.map((s) => userById.get(s.recipientUserId)?.name ?? '—').join(', ') || '—'
        const date = new Date(kind === 'inbox' ? (mySig?.sentAt ?? doc.createdAt) : doc.createdAt)
        const unread = kind === 'inbox' && mySig?.openedAt == null && status === 'pending'
        return (
          <Link
            key={doc.id}
            to="/documents/$id"
            params={{ id: doc.id }}
            className={cn(
              'flex items-start gap-3 px-5 py-3.5 hover:bg-bg-overlay/40 transition-colors',
              unread && 'bg-accent/5',
            )}
          >
            {unread ? (
              <span
                aria-hidden
                className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0"
                title="Ungelesen"
              />
            ) : (
              <span className="mt-1.5 h-2 w-2 shrink-0" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <p
                  className={cn(
                    'truncate text-sm text-text-primary',
                    unread ? 'font-semibold' : 'font-medium',
                  )}
                  title={doc.filename}
                >
                  {doc.filename}
                </p>
                {status && <StatusPill status={status} />}
              </div>
              <p className="mt-0.5 text-xs text-text-tertiary truncate">
                {kind === 'inbox' ? 'Von' : 'An'} {counterparty}
              </p>
            </div>
            <span className="text-[11px] text-text-tertiary tabular-nums whitespace-nowrap shrink-0">
              {date.toLocaleDateString('de-DE')}
            </span>
          </Link>
        )
      })}
    </div>
  )
}

// ============================================================================
// Fetched documents view — Belegeingang (admin only)
// ============================================================================

// ============================================================================
// QR Code TOTP Extraction (shared with settings-fetcher)
// ============================================================================

async function extractTotpFromQrImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas-Context konnte nicht erstellt werden'))
        return
      }
      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const code = jsQR(imageData.data, imageData.width, imageData.height)
      if (!code) {
        reject(new Error('QR-Code konnte nicht gelesen werden'))
        return
      }
      const url = code.data
      if (!url.startsWith('otpauth://')) {
        reject(new Error('Kein gültiger TOTP QR-Code'))
        return
      }
      const params = new URLSearchParams(url.split('?')[1] || '')
      const secret = params.get('secret')
      if (!secret) {
        reject(new Error('Kein Secret im QR-Code gefunden'))
        return
      }
      resolve(secret)
    }
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'))
    img.src = URL.createObjectURL(file)
  })
}

// ============================================================================
// Fetched documents view — Belegeingang (admin only) — full data-table pattern
// ============================================================================

const FETCHED_SORT_ACCESSORS: Record<string, (row: FetchedDocument) => unknown> = {
  filename: (r) => r.filename,
  documentNumber: (r) => r.documentNumber,
  type: (r) => r.type,
  date: (r) => (r.documentDate ? new Date(`${r.documentDate}T00:00:00`) : new Date(r.createdAt)),
  amountCents: (r) => r.amountCents,
  status: (r) => r.status,
  createdAt: (r) => new Date(r.createdAt),
}

const FETCHED_SEARCHABLE: ((row: FetchedDocument) => unknown)[] = [
  (r) => r.filename,
  (r) => r.vendorName,
  (r) => r.documentNumber,
  (r) => r.externalId,
]

const STATUS_CYCLE: FetchedDocumentStatus[] = ['new', 'reviewed', 'booked']

function FetchedDocsView({
  documents,
  accounts,
  providers,
}: {
  documents: FetchedDocument[]
  accounts: FetcherAccount[]
  providers: FetcherProviderType[]
}) {
  const queryClient = useQueryClient()

  // Filter state (applied before data-table)
  const [statusFilter, setStatusFilter] = useState<'all' | FetchedDocumentStatus>('all')
  const [typeFilter, setTypeFilter] = useState<'all' | 'invoice' | 'credit_note'>('all')
  const [accountFilter, setAccountFilter] = useState<'all' | string>('all')

  // PDF preview state
  const [previewDocId, setPreviewDocId] = useState<string | null>(null)

  // Account management state
  const [accountsExpanded, setAccountsExpanded] = useState(false)
  const [addAccountOpen, setAddAccountOpen] = useState(false)
  const [editAccountTarget, setEditAccountTarget] = useState<FetcherAccount | null>(null)
  const [twoFaTarget, setTwoFaTarget] = useState<string | null>(null)

  // Check if any account is awaiting 2FA
  const awaitingAccount = accounts.find((a) => a.status === 'awaiting-2fa')
  useEffect(() => {
    if (awaitingAccount && twoFaTarget !== awaitingAccount.id) {
      setTwoFaTarget(awaitingAccount.id)
    }
  }, [awaitingAccount, twoFaTarget])

  const accountMap = new Map(accounts.map((a) => [a.id, a]))

  // Pre-filter by status/type/account before handing to useDataTable
  const preFiltered = useMemo(() => {
    return documents.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false
      if (typeFilter !== 'all' && d.type !== typeFilter) return false
      if (accountFilter !== 'all' && d.accountId !== accountFilter) return false
      return true
    })
  }, [documents, statusFilter, typeFilter, accountFilter])

  const table = useDataTable<FetchedDocument>(preFiltered, FETCHED_SORT_ACCESSORS, {
    searchableFields: FETCHED_SEARCHABLE,
    defaultSortKey: 'createdAt',
    defaultSortDir: 'desc',
    defaultPageSize: 25,
    storageKey: 'fetched-docs',
  })

  // Mutations
  const bulkStatus = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: string }) =>
      api.bulkUpdateFetchedDocumentStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetcher'] })
      table.clearSelection()
      notify.success('Status aktualisiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const singleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.updateFetchedDocumentStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetcher'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteFetcherAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetcher'] })
      notify.success('Konto gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.syncFetcherAccount(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fetcher'] })
      if (data.alreadySyncing) {
        notify.warning('Synchronisation läuft bereits')
      } else {
        notify.success('Synchronisation gestartet')
      }
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function cycleStatus(doc: FetchedDocument) {
    const idx = STATUS_CYCLE.indexOf(doc.status)
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]
    singleStatusMutation.mutate({ id: doc.id, status: next })
  }

  const selectedIds = Array.from(table.selectedIds)

  // Derive preview doc + navigation
  const previewDoc = previewDocId
    ? (table.rows.find((d) => d.id === previewDocId) ??
      preFiltered.find((d) => d.id === previewDocId) ??
      null)
    : null
  const previewIdx = previewDocId ? table.rows.findIndex((d) => d.id === previewDocId) : -1
  const hasPrev = previewIdx > 0
  const hasNext = previewIdx >= 0 && previewIdx < table.rows.length - 1

  useEffect(() => {
    if (!previewDocId) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        if (hasPrev) {
          e.preventDefault()
          setPreviewDocId(table.rows[previewIdx - 1].id)
        }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        if (hasNext) {
          e.preventDefault()
          setPreviewDocId(table.rows[previewIdx + 1].id)
        }
      } else if (e.key === 'Escape') {
        setPreviewDocId(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewDocId, hasPrev, hasNext, previewIdx, table.rows])

  // Compact list row for split view
  const renderCompactRow = (doc: FetchedDocument) => {
    const isActive = doc.id === previewDocId
    const acc = accountMap.get(doc.accountId)
    return (
      <button
        key={doc.id}
        type="button"
        onClick={() => setPreviewDocId(doc.id)}
        className={cn(
          'w-full text-left px-3 py-2 transition-colors border-l-2',
          isActive ? 'bg-accent/10 border-l-accent' : 'border-l-transparent hover:bg-bg-overlay/40',
        )}
      >
        {/* Row 1: Nr/Name + Status + Typ */}
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-xs font-medium text-text-primary truncate flex-1 min-w-0">
            {doc.documentNumber ?? doc.filename}
          </p>
          <span
            className={cn(
              'shrink-0 px-1 py-px text-2xs uppercase tracking-wide rounded font-medium leading-tight',
              doc.type === 'credit_note'
                ? 'bg-success/10 text-success'
                : 'bg-accent/10 text-accent',
            )}
          >
            {doc.type === 'credit_note' ? 'GS' : 'RE'}
          </span>
          <span
            className={cn(
              'shrink-0 w-1.5 h-1.5 rounded-full',
              doc.status === 'new'
                ? 'bg-accent'
                : doc.status === 'reviewed'
                  ? 'bg-success'
                  : 'bg-text-tertiary',
            )}
            title={doc.status === 'new' ? 'Neu' : doc.status === 'reviewed' ? 'Geprüft' : 'Gebucht'}
          />
        </div>
        {/* Row 2: Betrag brutto + USt (always visible) */}
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="text-2xs font-medium text-text-secondary tabular-nums">
            {doc.amountCents != null
              ? (doc.amountCents / 100).toLocaleString('de-DE', {
                  style: 'currency',
                  currency: doc.currency ?? 'EUR',
                })
              : '—'}
          </span>
          <span className="text-2xs text-text-tertiary tabular-nums shrink-0">
            {doc.taxRate && doc.taxAmountCents != null
              ? `${doc.taxRate} USt. ${(doc.taxAmountCents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`
              : doc.taxRate
                ? `${doc.taxRate} USt.`
                : doc.taxAmountCents != null
                  ? `USt. ${(doc.taxAmountCents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`
                  : ''}
          </span>
        </div>
        {/* Row 3: Datum + Lieferant/Konto */}
        <div className="flex items-center gap-1 mt-px min-w-0 h-3.5 leading-[14px]">
          <span className="text-2xs text-text-tertiary tabular-nums shrink-0">
            {doc.documentDate
              ? new Date(`${doc.documentDate}T00:00:00`).toLocaleDateString('de-DE')
              : new Date(doc.createdAt).toLocaleDateString('de-DE')}
          </span>
          {(doc.vendorName || acc) && <span className="text-2xs text-text-tertiary">·</span>}
          <span className="text-2xs text-text-tertiary truncate">
            {doc.vendorName ?? acc?.label ?? ''}
          </span>
        </div>
      </button>
    )
  }

  // Split view: PDF preview center + compact list right
  if (previewDoc) {
    const acc = accountMap.get(previewDoc.accountId)
    return (
      <div className="space-y-4">
        {/* Toolbar stays above */}
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 px-3 py-1">
          <TableToolbar
            search={table.search}
            onSearch={table.setSearch}
            filteredRows={table.filteredRows}
            totalRows={table.totalRows}
            pageSize={table.pageSize}
            onPageSize={table.setPageSize}
            rightSlot={
              <div className="flex items-center gap-2">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  className="h-8 w-32 text-xs"
                  aria-label="Status filtern"
                >
                  <option value="all">Alle Status</option>
                  <option value="new">Neu</option>
                  <option value="reviewed">Geprüft</option>
                  <option value="booked">Gebucht</option>
                </Select>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
                  className="h-8 w-32 text-xs"
                  aria-label="Typ filtern"
                >
                  <option value="all">Alle Typen</option>
                  <option value="invoice">Rechnung</option>
                  <option value="credit_note">Gutschrift</option>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewDocId(null)}
                  title="Vorschau schließen"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            }
          />
        </div>

        {/* Split: 40% PDF | 30% Bearbeitung | 30% Belegliste.
            <640: feste 500px statt der 100vh-Rechnung — die bezog sich auf
            die mitscrollende Topbar und wäre nach dem Shell-Umbau eine
            falsche Referenz (Bars sind jetzt Flex-Kinder, der Scroller endet
            an der Bar-Oberkante, nichts kann dahinter verschwinden).
            sm:/Desktop byte-identisch zur alten Inline-Style-Rechnung. */}
        <div className="flex gap-3 h-[500px] sm:h-[calc(100vh-260px)] sm:min-h-[500px]">
          {/* PDF Preview — 40% */}
          <div
            className="flex flex-col min-w-0 rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden"
            style={{ width: '40%' }}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-bg-overlay/40">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    disabled={!hasPrev}
                    onClick={() => hasPrev && setPreviewDocId(table.rows[previewIdx - 1].id)}
                    className="p-1 rounded-md hover:bg-bg-overlay disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <span className="text-[11px] text-text-tertiary tabular-nums">
                    {previewIdx + 1}/{table.rows.length}
                  </span>
                  <button
                    type="button"
                    disabled={!hasNext}
                    onClick={() => hasNext && setPreviewDocId(table.rows[previewIdx + 1].id)}
                    className="p-1 rounded-md hover:bg-bg-overlay disabled:opacity-30 transition-colors"
                  >
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
                <span className="text-xs font-medium text-text-primary truncate">
                  {previewDoc.documentNumber ?? previewDoc.filename}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <a
                  href={api.fetchedDocumentFileUrl(previewDoc.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg bg-accent text-bg-base hover:bg-accent/90 transition-colors"
                >
                  <Download className="h-3 w-3" /> PDF
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewDocId(null)}
                  className="p-1 rounded-md hover:bg-bg-overlay text-text-tertiary hover:text-text-primary transition-colors"
                  title="Schließen"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <iframe
                key={previewDoc.id}
                src={api.fetchedDocumentFileUrl(previewDoc.id)}
                className="w-full h-full"
                title={previewDoc.filename}
              />
            </div>
          </div>

          {/* Bearbeitungsfeld — 30% */}
          <div
            className="flex flex-col rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden"
            style={{ width: '30%' }}
          >
            <div className="px-4 py-2.5 border-b border-border-subtle bg-bg-overlay/40 flex items-center justify-between">
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Belegdaten
              </p>
              <button
                type="button"
                onClick={() => cycleStatus(previewDoc)}
                className="cursor-pointer"
              >
                <FetchedStatusPill status={previewDoc.status} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              <EditableMetadataForm doc={previewDoc} accountLabel={acc?.label ?? null} />
            </div>
          </div>

          {/* Belegliste — 30% */}
          <div
            className="flex flex-col rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden"
            style={{ width: '30%' }}
          >
            <div className="px-3 py-2.5 border-b border-border-subtle bg-bg-overlay/40">
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Belege ({table.filteredRows})
              </p>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-border-subtle">
              {table.rows.map(renderCompactRow)}
            </div>
            <div className="border-t border-border-subtle px-3 py-2">
              <Pager
                page={table.page}
                totalPages={table.totalPages}
                onPage={table.setPage}
                filteredRows={table.filteredRows}
                pageSize={table.pageSize}
              />
            </div>
          </div>
        </div>

        {/* Account management panels (shared between both views) */}
        {addAccountOpen && (
          <FetcherAddAccountPanel
            providers={providers}
            onClose={() => setAddAccountOpen(false)}
            onCreated={() => {
              setAddAccountOpen(false)
              queryClient.invalidateQueries({ queryKey: ['fetcher'] })
            }}
          />
        )}
        {editAccountTarget && (
          <FetcherEditAccountPanel
            account={editAccountTarget}
            onClose={() => setEditAccountTarget(null)}
            onSaved={() => {
              setEditAccountTarget(null)
              queryClient.invalidateQueries({ queryKey: ['fetcher'] })
            }}
          />
        )}
        {twoFaTarget && (
          <FetcherTwoFaDialog
            accountId={twoFaTarget}
            onClose={() => setTwoFaTarget(null)}
            onSubmitted={() => {
              setTwoFaTarget(null)
              queryClient.invalidateQueries({ queryKey: ['fetcher'] })
            }}
          />
        )}
      </div>
    )
  }

  // === Full table view (no preview) ===
  return (
    <div className="space-y-4">
      {/* Account management — collapsible section */}
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
        <button
          type="button"
          onClick={() => setAccountsExpanded((v) => !v)}
          className="flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-bg-overlay/40 transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-text-primary">
            <Settings2 className="h-4 w-4 text-text-tertiary" />
            Anbieter-Konten verwalten
            <span className="text-xs text-text-tertiary font-normal">({accounts.length})</span>
          </span>
          {accountsExpanded ? (
            <ChevronUp className="h-4 w-4 text-text-tertiary" />
          ) : (
            <ChevronDown className="h-4 w-4 text-text-tertiary" />
          )}
        </button>
        {accountsExpanded && (
          <div className="border-t border-border-subtle px-5 py-4 space-y-4">
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={() => setAddAccountOpen(true)}>
                <Plus className="h-4 w-4" /> Konto hinzufügen
              </Button>
            </div>
            {accounts.length === 0 ? (
              <div className="py-8 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                  <Download className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <p className="mt-3 text-sm font-medium text-text-primary">
                  Keine Konten eingerichtet
                </p>
                <p className="mt-1 text-xs text-text-tertiary max-w-sm mx-auto">
                  Füge ein Anbieterkonto hinzu, um automatisch Rechnungen und Gutschriften
                  abzurufen.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                {accounts.map((account) => (
                  <FetcherAccountRow
                    key={account.id}
                    account={account}
                    providers={providers}
                    onSync={() => syncMutation.mutate(account.id)}
                    onEdit={() => setEditAccountTarget(account)}
                    onDelete={() => {
                      void confirm({
                        title: `„${account.label}“ löschen?`,
                        description:
                          'Das Konto und alle abgerufenen Belege werden unwiderruflich gelöscht.',
                        variant: 'danger',
                        confirmLabel: 'Löschen',
                      }).then((ok) => {
                        if (ok) deleteMutation.mutate(account.id)
                      })
                    }}
                    syncing={syncMutation.isPending}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 px-3 py-1">
        <TableToolbar
          search={table.search}
          onSearch={table.setSearch}
          filteredRows={table.filteredRows}
          totalRows={table.totalRows}
          pageSize={table.pageSize}
          onPageSize={table.setPageSize}
          rightSlot={
            <div className="flex items-center gap-2">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="h-8 w-32 text-xs"
                aria-label="Status filtern"
              >
                <option value="all">Alle Status</option>
                <option value="new">Neu</option>
                <option value="reviewed">Geprüft</option>
                <option value="booked">Gebucht</option>
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
                className="h-8 w-32 text-xs"
                aria-label="Typ filtern"
              >
                <option value="all">Alle Typen</option>
                <option value="invoice">Rechnung</option>
                <option value="credit_note">Gutschrift</option>
              </Select>
              {accounts.length > 1 && (
                <Select
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  className="h-8 w-40 text-xs"
                  aria-label="Konto filtern"
                >
                  <option value="all">Alle Konten</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          }
        />
      </div>

      {/* Document table */}
      <TableShell className="rounded-2xl bg-bg-elevated/40">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-bg-overlay/40">
            <tr className="text-left">
              <th className="pl-5 pr-2 py-2.5 w-10">
                <SelectAllCheckbox
                  checked={table.allOnPageSelected}
                  indeterminate={table.someOnPageSelected}
                  onToggle={table.toggleAllOnPage}
                />
              </th>
              <th className="px-5 py-2.5">
                <SortableHeader
                  label="Datei"
                  sortKey="filename"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-5 py-2.5 w-36">
                <SortableHeader
                  label="Nr."
                  sortKey="documentNumber"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-5 py-2.5 w-28">
                <SortableHeader
                  label="Typ"
                  sortKey="type"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-5 py-2.5 w-40 text-xs uppercase tracking-wider text-text-tertiary font-medium">
                Konto
              </th>
              <th className="px-5 py-2.5 w-28">
                <SortableHeader
                  label="Datum"
                  sortKey="date"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-5 py-2.5 w-28">
                <SortableHeader
                  label="Betrag"
                  sortKey="amountCents"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                  align="right"
                />
              </th>
              <th className="px-5 py-2.5 w-24">
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-5 py-2.5 w-20" aria-label="Aktionen" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {table.rows.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  {documents.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                        <Download className="h-7 w-7" strokeWidth={1.5} />
                      </div>
                      <p className="mt-4 text-base font-medium text-text-primary">
                        Noch keine Belege abgerufen
                      </p>
                      <p className="mt-1 text-sm text-text-tertiary max-w-sm mx-auto">
                        Richte oben ein Anbieterkonto ein und starte den ersten Sync.
                      </p>
                    </div>
                  ) : (
                    <EmptyTable
                      message="Keine Belege mit diesen Filtern."
                      hint="Passe Suche oder Filter an."
                    />
                  )}
                </td>
              </tr>
            ) : (
              table.rows.map((doc) => {
                const isSel = table.isSelected(doc.id)
                const acc = accountMap.get(doc.accountId)
                return (
                  <tr
                    key={doc.id}
                    onClick={() => setPreviewDocId(doc.id)}
                    className={cn(
                      'group transition-colors hover:bg-bg-overlay/40 cursor-pointer',
                      isSel && 'bg-accent/5',
                    )}
                  >
                    <td className="pl-5 pr-2 py-3">
                      <RowCheckbox
                        checked={isSel}
                        onToggle={() => table.toggleRow(doc.id)}
                        label={`${doc.filename} auswählen`}
                      />
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 text-text-tertiary shrink-0" />
                        <div className="min-w-0">
                          <p
                            className="truncate font-medium text-text-primary"
                            title={doc.filename}
                          >
                            {doc.filename}
                          </p>
                          {doc.vendorName && (
                            <p className="text-xs text-text-tertiary truncate">{doc.vendorName}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td
                      className="px-5 py-3 text-xs text-text-primary tabular-nums truncate"
                      title={doc.documentNumber ?? undefined}
                    >
                      {doc.documentNumber ?? '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-md font-medium',
                          doc.type === 'credit_note'
                            ? 'bg-success/10 text-success'
                            : 'bg-accent/10 text-accent',
                        )}
                      >
                        {doc.type === 'credit_note' ? 'Gutschrift' : 'Rechnung'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-text-tertiary truncate">
                      {acc?.label ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-xs text-text-tertiary tabular-nums whitespace-nowrap">
                      {doc.documentDate
                        ? new Date(`${doc.documentDate}T00:00:00`).toLocaleDateString('de-DE')
                        : new Date(doc.createdAt).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-5 py-3 text-right text-xs text-text-tertiary tabular-nums whitespace-nowrap">
                      {doc.amountCents != null
                        ? (doc.amountCents / 100).toLocaleString('de-DE', {
                            style: 'currency',
                            currency: doc.currency ?? 'EUR',
                          })
                        : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          cycleStatus(doc)
                        }}
                        title="Status ändern"
                        className="cursor-pointer"
                      >
                        <FetchedStatusPill status={doc.status} />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <a
                        href={api.fetchedDocumentFileUrl(doc.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Herunterladen"
                        title="PDF öffnen"
                        onClick={(e) => e.stopPropagation()}
                        className="text-text-tertiary hover:text-accent transition-colors p-2 rounded-md hover:bg-bg-overlay opacity-0 group-hover:opacity-100 touch:opacity-100"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </TableShell>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />

      <BulkActionBar
        selectionCount={table.selectionCount}
        filteredRows={table.filteredRows}
        onClear={table.clearSelection}
        onSelectAllFiltered={table.selectAllFiltered}
      >
        <Button
          size="sm"
          onClick={() => bulkStatus.mutate({ ids: selectedIds, status: 'reviewed' })}
          disabled={bulkStatus.isPending}
        >
          <CheckCircle className="h-3.5 w-3.5" /> Als geprüft
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => bulkStatus.mutate({ ids: selectedIds, status: 'booked' })}
          disabled={bulkStatus.isPending}
        >
          Als gebucht
        </Button>
      </BulkActionBar>

      {addAccountOpen && (
        <FetcherAddAccountPanel
          providers={providers}
          onClose={() => setAddAccountOpen(false)}
          onCreated={() => {
            setAddAccountOpen(false)
            queryClient.invalidateQueries({ queryKey: ['fetcher'] })
          }}
        />
      )}
      {editAccountTarget && (
        <FetcherEditAccountPanel
          account={editAccountTarget}
          onClose={() => setEditAccountTarget(null)}
          onSaved={() => {
            setEditAccountTarget(null)
            queryClient.invalidateQueries({ queryKey: ['fetcher'] })
          }}
        />
      )}
      {twoFaTarget && (
        <FetcherTwoFaDialog
          accountId={twoFaTarget}
          onClose={() => setTwoFaTarget(null)}
          onSubmitted={() => {
            setTwoFaTarget(null)
            queryClient.invalidateQueries({ queryKey: ['fetcher'] })
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Fetcher Account Row (inline in account management section)
// ============================================================================

function FetcherAccountRow({
  account,
  providers,
  onSync,
  onEdit,
  onDelete,
  syncing,
}: {
  account: FetcherAccount
  providers: FetcherProviderType[]
  onSync: () => void
  onEdit: () => void
  onDelete: () => void
  syncing: boolean
}) {
  const provider = providers.find((p) => p.id === account.provider)
  const providerName = provider?.name ?? account.provider

  return (
    <div className="flex items-center gap-4 rounded-xl border border-border-subtle bg-bg-overlay/30 px-4 py-3">
      {/* Provider icon */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
        {account.provider === 'amazon-business' ? (
          <span className="text-sm font-bold">A</span>
        ) : (
          <Download className="h-4 w-4" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-text-primary truncate">{account.label}</span>
          <FetcherAccountStatusBadge status={account.status} />
          {account.hasTotp && (
            <span className="inline-flex items-center gap-1 text-2xs uppercase tracking-wide text-success bg-success/10 px-1.5 py-0.5 rounded-md font-medium">
              <ShieldCheck className="h-3 w-3" /> TOTP
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-text-tertiary mt-0.5">
          <span>{providerName}</span>
          <span>{account.email}</span>
          {account.lastSyncAt && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(account.lastSyncAt).toLocaleString('de-DE')}
            </span>
          )}
        </div>
        {account.lastSyncError && (
          <div className="mt-1 text-xs text-danger inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {account.lastSyncError}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSync}
          disabled={syncing || account.status === 'syncing'}
          title="Sync starten"
        >
          {account.status === 'syncing' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} title="Bearbeiten">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} title="Löschen">
          <Trash2 className="h-4 w-4 text-danger" />
        </Button>
      </div>
    </div>
  )
}

function FetcherAccountStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: 'Aktiv', cls: 'bg-success/15 text-success border-success/20' },
    syncing: { label: 'Synchronisiert …', cls: 'bg-accent/15 text-accent border-accent/20' },
    'awaiting-2fa': {
      label: '2FA erforderlich',
      cls: 'bg-warning/15 text-warning border-warning/20',
    },
    error: { label: 'Fehler', cls: 'bg-danger/15 text-danger border-danger/20' },
  }
  const m = map[status] ?? map.active
  return (
    <span
      className={`px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  )
}

// ============================================================================
// Fetcher Account Add/Edit/2FA Panels (integrated into documents page)
// ============================================================================

function FetcherAddAccountPanel({
  providers,
  onClose,
  onCreated,
}: {
  providers: FetcherProviderType[]
  onClose: () => void
  onCreated: () => void
}) {
  const [provider, setProvider] = useState(providers[0]?.id ?? '')
  const [label, setLabel] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpSecret, setTotpSecret] = useState('')
  const [syncFromDate, setSyncFromDate] = useState('')

  const createMutation = useMutation({
    mutationFn: () =>
      api.createFetcherAccount({
        provider,
        label,
        email,
        password,
        totpSecret: totpSecret.trim() || null,
        syncFromDate: syncFromDate || null,
      }),
    onSuccess: () => {
      notify.success('Konto erstellt')
      onCreated()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const canSubmit = provider && label.trim() && email.trim() && password.trim()

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Abrufkonto hinzufügen"
      description="Zugangsdaten für den automatischen Belegabruf."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="fd-provider">Anbieter</Label>
          <Select id="fd-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fd-label">Bezeichnung</Label>
          <Input
            id="fd-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="z.B. IT Financeflow Amazon"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fd-email">E-Mail</Label>
          <Input
            id="fd-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="login@firma.de"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fd-password">Passwort</Label>
          <Input
            id="fd-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fd-totp">TOTP-Secret (optional)</Label>
          <div className="flex gap-2">
            <Input
              id="fd-totp"
              type="password"
              value={totpSecret}
              onChange={(e) => setTotpSecret(e.target.value)}
              placeholder="Base32-Secret"
            />
            <label className="shrink-0">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  try {
                    const secret = await extractTotpFromQrImage(file)
                    setTotpSecret(secret)
                    notify.success('TOTP-Secret aus QR-Code extrahiert')
                  } catch (err) {
                    notify.error((err as Error).message)
                  }
                  e.target.value = ''
                }}
              />
              <span className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-border-default bg-bg-base text-text-secondary hover:text-text-primary cursor-pointer text-sm touch:opacity-100">
                <QrCode className="h-4 w-4 mr-1.5" />
                QR scannen
              </span>
            </label>
          </div>
          <p className="text-xs text-text-tertiary">
            TOTP-Secret oder QR-Code deiner Authenticator-App. Wird verschlüsselt gespeichert.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fd-syncfrom">Rechnungen ab (optional)</Label>
          <Input
            id="fd-syncfrom"
            type="date"
            value={syncFromDate}
            onChange={(e) => setSyncFromDate(e.target.value)}
          />
          <p className="text-xs text-text-tertiary">
            Nur Rechnungen ab diesem Datum abrufen. Leer = alle verfügbaren.
          </p>
        </div>
      </div>
    </SidePanel>
  )
}

function FetcherEditAccountPanel({
  account,
  onClose,
  onSaved,
}: {
  account: FetcherAccount
  onClose: () => void
  onSaved: () => void
}) {
  const [label, setLabel] = useState(account.label)
  const [email, setEmail] = useState(account.email)
  const [password, setPassword] = useState('')
  const [totpSecret, setTotpSecret] = useState('')
  const [syncFromDate, setSyncFromDate] = useState(account.syncFromDate ?? '')

  const updateMutation = useMutation({
    mutationFn: () => {
      const data: Record<string, unknown> = {}
      if (label.trim() !== account.label) data.label = label.trim()
      if (email.trim() !== account.email) data.email = email.trim()
      if (password.trim()) data.password = password.trim()
      if (totpSecret.trim()) data.totpSecret = totpSecret.trim()
      const newSyncFrom = syncFromDate || null
      if (newSyncFrom !== (account.syncFromDate ?? null)) data.syncFromDate = newSyncFrom
      return api.updateFetcherAccount(account.id, data)
    },
    onSuccess: () => {
      notify.success('Gespeichert')
      onSaved()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Konto bearbeiten"
      description={account.label}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="fde-label">Bezeichnung</Label>
          <Input id="fde-label" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fde-email">E-Mail</Label>
          <Input
            id="fde-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fde-password">Neues Passwort (leer lassen = unverändert)</Label>
          <Input
            id="fde-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fde-totp">Neues TOTP-Secret (leer = unverändert)</Label>
          <div className="flex gap-2">
            <Input
              id="fde-totp"
              type="password"
              value={totpSecret}
              onChange={(e) => setTotpSecret(e.target.value)}
            />
            <label className="shrink-0">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  try {
                    const secret = await extractTotpFromQrImage(file)
                    setTotpSecret(secret)
                    notify.success('TOTP-Secret aus QR-Code extrahiert')
                  } catch (err) {
                    notify.error((err as Error).message)
                  }
                  e.target.value = ''
                }}
              />
              <span className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-border-default bg-bg-base text-text-secondary hover:text-text-primary cursor-pointer text-sm touch:opacity-100">
                <QrCode className="h-4 w-4 mr-1.5" />
                QR scannen
              </span>
            </label>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fde-syncfrom">Rechnungen ab</Label>
          <Input
            id="fde-syncfrom"
            type="date"
            value={syncFromDate}
            onChange={(e) => setSyncFromDate(e.target.value)}
          />
          <p className="text-xs text-text-tertiary">
            Nur Rechnungen ab diesem Datum abrufen. Leer = alle verfügbaren.
          </p>
        </div>
      </div>
    </SidePanel>
  )
}

function FetcherTwoFaDialog({
  accountId,
  onClose,
  onSubmitted,
}: {
  accountId: string
  onClose: () => void
  onSubmitted: () => void
}) {
  const [code, setCode] = useState('')

  const submitMutation = useMutation({
    mutationFn: () => api.submitFetcher2fa(accountId, code),
    onSuccess: () => {
      notify.success('2FA-Code übermittelt')
      onSubmitted()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="2FA-Code eingeben"
      description="Der Sync wartet auf einen Bestätigungscode."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!code.trim() || submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            <KeyRound className="h-4 w-4" />
            Code senden
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="rounded-lg bg-warning/5 border border-warning/20 px-4 py-3 text-sm text-warning">
          <AlertTriangle className="inline h-4 w-4 mr-2" />
          Der Anbieter verlangt einen 2FA-Bestätigungscode. Gib den Code aus deiner
          Authenticator-App ein.
        </div>
        <div className="space-y-2">
          <Label htmlFor="ftfa-code">Code</Label>
          <Input
            id="ftfa-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            autoFocus
            maxLength={10}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.trim()) submitMutation.mutate()
            }}
          />
        </div>
      </div>
    </SidePanel>
  )
}

function FetchedStatusPill({ status }: { status: FetchedDocumentStatus }) {
  const map: Record<FetchedDocumentStatus, { label: string; cls: string }> = {
    new: { label: 'Neu', cls: 'bg-accent/15 text-accent border-accent/20' },
    reviewed: { label: 'Geprüft', cls: 'bg-success/15 text-success border-success/20' },
    booked: { label: 'Gebucht', cls: 'bg-bg-overlay text-text-tertiary border-border-default' },
  }
  const m = map[status] ?? map.new
  return (
    <span
      className={`px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  )
}

// ============================================================================
// Inline edit helper + editable metadata bar
// ============================================================================

function EditableMetadataForm({
  doc,
  accountLabel,
}: {
  doc: FetchedDocument
  accountLabel: string | null
}) {
  const queryClient = useQueryClient()
  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.updateFetchedDocument>[1]) =>
      api.updateFetchedDocument(doc.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetcher'] })
      notify.success('Gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const [docNumber, setDocNumber] = useState(doc.documentNumber ?? '')
  const [docDate, setDocDate] = useState(doc.documentDate ?? '')
  const [amountStr, setAmountStr] = useState(
    doc.amountCents != null
      ? (doc.amountCents / 100).toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '',
  )
  const [netStr, setNetStr] = useState(
    doc.amountNetCents != null
      ? (doc.amountNetCents / 100).toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '',
  )
  const [taxStr, setTaxStr] = useState(
    doc.taxAmountCents != null
      ? (doc.taxAmountCents / 100).toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '',
  )
  const [taxRate, setTaxRate] = useState(doc.taxRate ?? '')
  const [vendor, setVendor] = useState(doc.vendorName ?? '')
  const [docType, setDocType] = useState(doc.type)

  // Reset when doc changes
  useEffect(() => {
    setDocNumber(doc.documentNumber ?? '')
    setDocDate(doc.documentDate ?? '')
    setAmountStr(
      doc.amountCents != null
        ? (doc.amountCents / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '',
    )
    setNetStr(
      doc.amountNetCents != null
        ? (doc.amountNetCents / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '',
    )
    setTaxStr(
      doc.taxAmountCents != null
        ? (doc.taxAmountCents / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '',
    )
    setTaxRate(doc.taxRate ?? '')
    setVendor(doc.vendorName ?? '')
    setDocType(doc.type)
  }, [
    doc.documentNumber,
    doc.documentDate,
    doc.amountCents,
    doc.amountNetCents,
    doc.taxAmountCents,
    doc.taxRate,
    doc.vendorName,
    doc.type,
  ])

  const parseCents = (s: string): number | null => {
    const cleaned = s
      .replace(/\s/g, '')
      .replace(/\.(?=\d{3})/g, '')
      .replace(',', '.')
    const n = Number.parseFloat(cleaned)
    return Number.isNaN(n) ? null : Math.round(n * 100)
  }

  const handleSave = () => {
    updateMutation.mutate({
      documentNumber: docNumber || null,
      documentDate: docDate || null,
      amountCents: parseCents(amountStr),
      amountNetCents: parseCents(netStr),
      taxAmountCents: parseCents(taxStr),
      taxRate: taxRate || null,
      vendorName: vendor || null,
      type: docType,
    })
  }

  const fieldClass =
    'w-full rounded-lg border border-border-subtle bg-bg-overlay/50 px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none transition-colors'
  const labelClass =
    'block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1'

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="ed-docnr" className={labelClass}>
          Rechnungsnummer
        </label>
        <input
          className={cn(fieldClass, 'tabular-nums')}
          id="ed-docnr"
          value={docNumber}
          onChange={(e) => setDocNumber(e.target.value)}
          onBlur={handleSave}
          placeholder="z.B. DE64LCG8FAEUD"
        />
      </div>
      <div>
        <label htmlFor="ed-date" className={labelClass}>
          Rechnungsdatum
        </label>
        <input
          type="date"
          className={fieldClass}
          id="ed-date"
          value={docDate}
          onChange={(e) => {
            setDocDate(e.target.value)
            setTimeout(handleSave, 0)
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="ed-brutto" className={labelClass}>
            Brutto (€)
          </label>
          <input
            className={cn(fieldClass, 'tabular-nums')}
            id="ed-brutto"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            onBlur={handleSave}
            placeholder="0,00"
          />
        </div>
        <div>
          <label htmlFor="ed-netto" className={labelClass}>
            Netto (€)
          </label>
          <input
            className={cn(fieldClass, 'tabular-nums')}
            id="ed-netto"
            value={netStr}
            onChange={(e) => setNetStr(e.target.value)}
            onBlur={handleSave}
            placeholder="0,00"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="ed-tax" className={labelClass}>
            USt-Betrag (€)
          </label>
          <input
            className={cn(fieldClass, 'tabular-nums')}
            id="ed-tax"
            value={taxStr}
            onChange={(e) => setTaxStr(e.target.value)}
            onBlur={handleSave}
            placeholder="0,00"
          />
        </div>
        <div>
          <label htmlFor="ed-taxrate" className={labelClass}>
            Steuerart
          </label>
          <select
            className={fieldClass}
            id="ed-taxrate"
            value={taxRate}
            onChange={(e) => {
              setTaxRate(e.target.value)
              setTimeout(handleSave, 0)
            }}
          >
            <option value="">— Unbekannt —</option>
            <option value="19%">19% MwSt.</option>
            <option value="7%">7% MwSt.</option>
            <option value="0%">0% Steuerbefreit</option>
            <option value="RVC">Reverse Charge</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="ed-vendor" className={labelClass}>
          Lieferant
        </label>
        <input
          className={fieldClass}
          id="ed-vendor"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          onBlur={handleSave}
          placeholder="Firmenname"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="ed-type" className={labelClass}>
            Belegtyp
          </label>
          <select
            className={fieldClass}
            id="ed-type"
            value={docType}
            onChange={(e) => {
              setDocType(e.target.value as FetchedDocumentType)
              setTimeout(handleSave, 0)
            }}
          >
            <option value="invoice">Rechnung</option>
            <option value="credit_note">Gutschrift</option>
          </select>
        </div>
        {accountLabel && (
          <div>
            <label htmlFor="ed-konto" className={labelClass}>
              Konto
            </label>
            <p className="text-xs text-text-primary px-2.5 py-1.5">{accountLabel}</p>
          </div>
        )}
      </div>
      <div className="pt-1">
        <p className="text-2xs text-text-tertiary">
          {doc.filename} · {(doc.sizeBytes / 1024).toFixed(0)} KB
        </p>
      </div>
    </div>
  )
}

// ============================================================================
// Small atoms
// ============================================================================

function ViewChip({
  active,
  onClick,
  children,
  ...rest
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'p-1.5 rounded-md transition-colors',
        active
          ? 'bg-bg-elevated text-text-primary shadow-sm'
          : 'text-text-tertiary hover:text-text-primary',
      )}
      {...rest}
    >
      {children}
    </button>
  )
}

function IconButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="text-text-tertiary hover:text-accent"
      onClick={(e) => {
        e.stopPropagation()
        rest.onClick?.(e)
      }}
      {...rest}
    >
      {children}
    </Button>
  )
}

function aggregateStatus(sigs: DocSignature[]): DocSignature['status'] | undefined {
  if (sigs.length === 0) return undefined
  if (sigs.every((s) => s.status === 'signed')) return 'signed'
  if (sigs.some((s) => s.status === 'rejected')) return 'rejected'
  return 'pending'
}

function StatusPill({ status }: { status: DocSignature['status'] }) {
  const map: Record<DocSignature['status'], { label: string; cls: string }> = {
    pending: { label: 'Offen', cls: 'bg-warning/15 text-warning border-warning/20' },
    signed: { label: 'Unterzeichnet', cls: 'bg-success/15 text-success border-success/20' },
    rejected: { label: 'Abgelehnt', cls: 'bg-danger/15 text-danger border-danger/20' },
  }
  const m = map[status]
  return (
    <span
      className={`px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  )
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

// ============================================================================
// Dialogs
// ============================================================================

function UploadDialog({
  allUsers,
  projects,
  onClose,
  onSubmit,
}: {
  allUsers: { id: string; name: string; email: string }[]
  projects: { id: string; name: string }[]
  onClose: () => void
  onSubmit: (data: {
    file: File
    recipientUserIds: string[]
    message: string
    projectId: string | null
  }) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const [projectId, setProjectId] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const toggle = (id: string) => {
    setSelected((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null)
  }

  return (
    <SidePanel
      open
      onClose={onClose}
      title="PDF hochladen"
      description="Empfänger sind optional — du kannst die Datei auch erstmal nur ablegen und später verschicken."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!file || busy}
            onClick={async () => {
              if (!file) return
              setBusy(true)
              try {
                await onSubmit({
                  file,
                  recipientUserIds: Array.from(selected),
                  message,
                  projectId: projectId || null,
                })
              } finally {
                setBusy(false)
              }
            }}
          >
            <Upload className="h-4 w-4" />
            Hochladen{selected.size > 0 ? ' & verschicken' : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div>
          <Label>Datei</Label>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={onFileChange}
            className="mt-2 block w-full text-sm text-text-primary file:mr-3 file:rounded-md file:border-0 file:bg-bg-overlay file:px-3 file:py-1.5 file:text-text-primary hover:file:bg-bg-overlay/80"
          />
          {file && (
            <p className="mt-1 text-xs text-text-tertiary">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}
        </div>
        <div>
          <Label>Projekt (optional)</Label>
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">— Kein Projekt —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Empfänger zur Unterschrift ({selected.size})</Label>
          <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-border-subtle divide-y divide-border-subtle">
            {allUsers.length === 0 ? (
              <p className="p-3 text-sm text-text-tertiary">Keine anderen Mitarbeiter vorhanden.</p>
            ) : (
              allUsers.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-bg-overlay cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    className="accent-accent"
                  />
                  <span className="text-sm text-text-primary">{u.name}</span>
                  <span className="text-xs text-text-tertiary ml-auto">{u.email}</span>
                </label>
              ))
            )}
          </div>
          <p className="mt-1 text-xs text-text-tertiary">
            Leer lassen, um nur hochzuladen ohne zu verschicken.
          </p>
        </div>
        <div>
          <Label>Notiz (optional)</Label>
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="z.B. Bitte bis Freitag unterschreiben"
          />
        </div>
      </div>
    </SidePanel>
  )
}

function RenameDialog({
  doc,
  projects,
  onClose,
  onSaved,
}: {
  doc: DocumentSummary
  projects: { id: string; name: string }[]
  onClose: () => void
  onSaved: () => void
}) {
  const [filename, setFilename] = useState(doc.filename)
  const [projectId, setProjectId] = useState<string>(doc.projectId ?? '')
  const save = useMutation({
    mutationFn: () =>
      api.updateDocument(doc.id, {
        filename: filename.trim() || undefined,
        projectId: projectId || null,
      }),
    onSuccess: () => {
      notify.success('Gespeichert')
      onSaved()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Datei bearbeiten"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!filename.trim() || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="rn-name">Dateiname</Label>
          <Input
            id="rn-name"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rn-proj">Projekt</Label>
          <Select id="rn-proj" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">— Kein Projekt —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </SidePanel>
  )
}

function SendDialog({
  doc,
  allUsers,
  onClose,
  onSent,
}: {
  doc: DocumentSummary
  allUsers: { id: string; name: string; email: string }[]
  onClose: () => void
  onSent: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const send = useMutation({
    mutationFn: () =>
      api.sendDocument(doc.id, {
        recipientUserIds: Array.from(selected),
        message: message || null,
      }),
    onSuccess: (r) => {
      notify.success(`An ${r.signatures.length} Empfänger verschickt`)
      onSent()
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const toggle = (id: string) => {
    setSelected((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  return (
    <SidePanel
      open
      onClose={onClose}
      title="An Empfänger senden"
      description={doc.filename}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={() => send.mutate()} disabled={selected.size === 0 || send.isPending}>
            <Send className="h-4 w-4" /> Senden
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div>
          <Label>Empfänger ({selected.size})</Label>
          <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-border-subtle divide-y divide-border-subtle">
            {allUsers.length === 0 ? (
              <p className="p-3 text-sm text-text-tertiary">Keine anderen Mitarbeiter vorhanden.</p>
            ) : (
              allUsers.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-bg-overlay cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    className="accent-accent"
                  />
                  <span className="text-sm text-text-primary">{u.name}</span>
                  <span className="text-xs text-text-tertiary ml-auto">{u.email}</span>
                </label>
              ))
            )}
          </div>
        </div>
        <div>
          <Label>Notiz (optional)</Label>
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="z.B. Bitte bis Freitag unterschreiben"
          />
        </div>
      </div>
    </SidePanel>
  )
}

export const documentsListRoute = Route
